package com.mencanhealth.admin.data

import com.mencanhealth.admin.model.*
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.Direction
import dev.gitlive.firebase.firestore.firestore
import kotlinx.datetime.Clock

class AdminAppointmentService {
    private val firestore = Firebase.firestore
    private val appointmentsCollection = firestore.collection("appointments")
    private val availabilityCollection = firestore.collection("availability")

    // === APPOINTMENTS ===

    suspend fun createAppointment(appointment: Appointment): String {
        val now = Clock.System.now().toEpochMilliseconds()
        val docRef = appointmentsCollection.document
        docRef.set(mapOf(
            "patientName" to appointment.patientName,
            "patientEmail" to appointment.patientEmail,
            "patientWhatsApp" to appointment.patientWhatsApp,
            "appointmentType" to appointment.appointmentType,
            "modality" to appointment.modality,
            "date" to appointment.date,
            "timeSlot" to appointment.timeSlot,
            "durationMinutes" to appointment.durationMinutes,
            "status" to AppointmentStatus.PENDING_PAYMENT.name,
            "paymentMethod" to appointment.paymentMethod,
            "paymentConfirmed" to false,
            "meetLink" to "",
            "notes" to appointment.notes,
            "questionnaireResponses" to appointment.questionnaireResponses,
            "createdAt" to now,
            "confirmedAt" to 0L
        ))
        return docRef.id
    }

    suspend fun listAppointments(statusFilter: String? = null): List<Appointment> {
        val snapshot = if (statusFilter != null) {
            appointmentsCollection
                .where { "status" equalTo statusFilter }
                .orderBy("date", Direction.DESCENDING)
                .get()
        } else {
            appointmentsCollection
                .orderBy("date", Direction.DESCENDING)
                .get()
        }
        return snapshot.documents.map { doc -> docToAppointment(doc) }
    }

    suspend fun getAppointment(id: String): Appointment? {
        return try {
            val doc = appointmentsCollection.document(id).get()
            if (!doc.exists) return null
            docToAppointment(doc)
        } catch (_: Exception) { null }
    }

    suspend fun updateAppointmentStatus(id: String, status: String) {
        if (status == AppointmentStatus.CONFIRMED.name) {
            val now = Clock.System.now().toEpochMilliseconds()
            appointmentsCollection.document(id).update(
                "status" to status,
                "confirmedAt" to now
            )
        } else {
            appointmentsCollection.document(id).update("status" to status)
        }
    }

    suspend fun confirmPayment(id: String) {
        val now = Clock.System.now().toEpochMilliseconds()
        appointmentsCollection.document(id).update(
            "paymentConfirmed" to true,
            "status" to AppointmentStatus.CONFIRMED.name,
            "confirmedAt" to now
        )
    }

    suspend fun setMeetLink(id: String, link: String) {
        appointmentsCollection.document(id).update("meetLink" to link)
    }

    suspend fun cancelAppointment(id: String) {
        appointmentsCollection.document(id).update("status" to AppointmentStatus.CANCELLED.name)
    }

    suspend fun markNoShow(id: String) {
        appointmentsCollection.document(id).update("status" to AppointmentStatus.NO_SHOW.name)
    }

    suspend fun markCompleted(id: String) {
        appointmentsCollection.document(id).update("status" to AppointmentStatus.COMPLETED.name)
    }

    suspend fun getTodayAppointments(): List<Appointment> {
        val today = todayDateString()
        val snapshot = appointmentsCollection
            .where { "date" equalTo today }
            .orderBy("timeSlot", Direction.ASCENDING)
            .get()
        return snapshot.documents.map { doc -> docToAppointment(doc) }
    }

    suspend fun getAppointmentStats(): AppointmentStats {
        val all = listAppointments()
        val today = todayDateString()
        val weekStart = weekStartDateString()
        val monthStart = monthStartDateString()

        val todayAppts = all.filter { it.date == today }
        val weekAppts = all.filter { it.date >= weekStart }
        val monthAppts = all.filter { it.date >= monthStart }

        return AppointmentStats(
            todayCount = todayAppts.size,
            weekCount = weekAppts.size,
            pendingPayment = all.count { it.status == AppointmentStatus.PENDING_PAYMENT.name },
            confirmed = all.count { it.status == AppointmentStatus.CONFIRMED.name },
            monthTotal = monthAppts.size,
            monthRevenue = monthAppts
                .filter { it.paymentConfirmed }
                .sumOf { appt ->
                    try { AppointmentType.valueOf(appt.appointmentType).priceMXN } catch (_: Exception) { 0 }
                }
        )
    }

    // === AVAILABILITY ===

    suspend fun addAvailabilityBlock(block: AvailabilityBlock): String {
        val docRef = availabilityCollection.document
        docRef.set(mapOf(
            "dayOfWeek" to block.dayOfWeek,
            "specificDate" to block.specificDate,
            "startTime" to block.startTime,
            "endTime" to block.endTime,
            "slotDurationMinutes" to block.slotDurationMinutes,
            "isRecurring" to block.isRecurring,
            "active" to block.active
        ))
        return docRef.id
    }

    suspend fun listAvailabilityBlocks(): List<AvailabilityBlock> {
        val snapshot = availabilityCollection.get()
        return snapshot.documents.map { doc ->
            AvailabilityBlock(
                id = doc.id,
                dayOfWeek = try { doc.get<Int>("dayOfWeek") } catch (_: Exception) { -1 },
                specificDate = try { doc.get<String>("specificDate") } catch (_: Exception) { "" },
                startTime = try { doc.get<String>("startTime") } catch (_: Exception) { "" },
                endTime = try { doc.get<String>("endTime") } catch (_: Exception) { "" },
                slotDurationMinutes = try { doc.get<Int>("slotDurationMinutes") } catch (_: Exception) { 30 },
                isRecurring = try { doc.get<Boolean>("isRecurring") } catch (_: Exception) { false },
                active = try { doc.get<Boolean>("active") } catch (_: Exception) { true }
            )
        }
    }

    suspend fun removeAvailabilityBlock(id: String) {
        availabilityCollection.document(id).delete()
    }

    suspend fun toggleAvailabilityBlock(id: String, active: Boolean) {
        availabilityCollection.document(id).update("active" to active)
    }

    suspend fun getAvailableSlots(date: String, dayOfWeek: Int): List<TimeSlot> {
        val blocks = listAvailabilityBlocks().filter { block ->
            block.active && (
                (block.isRecurring && block.dayOfWeek == dayOfWeek) ||
                (!block.isRecurring && block.specificDate == date)
            )
        }

        val allSlots = mutableListOf<String>()
        for (block in blocks) {
            val slots = generateSlotsFromBlock(block.startTime, block.endTime, block.slotDurationMinutes)
            allSlots.addAll(slots)
        }

        val bookedSnapshot = appointmentsCollection
            .where { "date" equalTo date }
            .get()
        val bookedTimes = bookedSnapshot.documents.mapNotNull { doc ->
            val status = try { doc.get<String>("status") } catch (_: Exception) { "" }
            if (status == AppointmentStatus.CANCELLED.name) null
            else try { doc.get<String>("timeSlot") } catch (_: Exception) { null }
        }.toSet()

        return allSlots.distinct().sorted().map { time ->
            TimeSlot(time = time, available = time !in bookedTimes)
        }
    }

    suspend fun expireOldPendingAppointments() {
        val cutoff = Clock.System.now().toEpochMilliseconds() - (48 * 60 * 60 * 1000)
        val snapshot = appointmentsCollection
            .where { "status" equalTo AppointmentStatus.PENDING_PAYMENT.name }
            .get()
        for (doc in snapshot.documents) {
            val createdAt = try { doc.get<Long>("createdAt") } catch (_: Exception) { 0L }
            if (createdAt > 0 && createdAt < cutoff) {
                appointmentsCollection.document(doc.id)
                    .update("status" to AppointmentStatus.CANCELLED.name)
            }
        }
    }

    // === HELPERS ===

    private fun docToAppointment(doc: dev.gitlive.firebase.firestore.DocumentSnapshot): Appointment {
        return Appointment(
            id = doc.id,
            patientName = try { doc.get<String>("patientName") } catch (_: Exception) { "" },
            patientEmail = try { doc.get<String>("patientEmail") } catch (_: Exception) { "" },
            patientWhatsApp = try { doc.get<String>("patientWhatsApp") } catch (_: Exception) { "" },
            appointmentType = try { doc.get<String>("appointmentType") } catch (_: Exception) { AppointmentType.INITIAL.name },
            modality = try { doc.get<String>("modality") } catch (_: Exception) { AppointmentModality.PRESENCIAL.name },
            date = try { doc.get<String>("date") } catch (_: Exception) { "" },
            timeSlot = try { doc.get<String>("timeSlot") } catch (_: Exception) { "" },
            durationMinutes = try { doc.get<Int>("durationMinutes") } catch (_: Exception) { 60 },
            status = try { doc.get<String>("status") } catch (_: Exception) { AppointmentStatus.PENDING_PAYMENT.name },
            paymentMethod = try { doc.get<String>("paymentMethod") } catch (_: Exception) { PaymentMethod.TRANSFER.name },
            paymentConfirmed = try { doc.get<Boolean>("paymentConfirmed") } catch (_: Exception) { false },
            meetLink = try { doc.get<String>("meetLink") } catch (_: Exception) { "" },
            notes = try { doc.get<String>("notes") } catch (_: Exception) { "" },
            createdAt = try { doc.get<Long>("createdAt") } catch (_: Exception) { 0L },
            confirmedAt = try { doc.get<Long>("confirmedAt") } catch (_: Exception) { 0L },
            questionnaireResponses = try { doc.get<String>("questionnaireResponses") } catch (_: Exception) { "" }
        )
    }

    private fun generateSlotsFromBlock(startTime: String, endTime: String, durationMinutes: Int): List<String> {
        val slots = mutableListOf<String>()
        var currentMinutes = timeToMinutes(startTime)
        val endMinutes = timeToMinutes(endTime)
        while (currentMinutes + durationMinutes <= endMinutes) {
            slots.add(minutesToTime(currentMinutes))
            currentMinutes += durationMinutes
        }
        return slots
    }

    private fun timeToMinutes(time: String): Int {
        val parts = time.split(":")
        return parts[0].toInt() * 60 + parts[1].toInt()
    }

    private fun minutesToTime(minutes: Int): String {
        val h = minutes / 60
        val m = minutes % 60
        return "${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}"
    }

    private fun todayDateString(): String {
        val d = kotlin.js.Date()
        return "${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}"
    }

    private fun weekStartDateString(): String {
        val d = kotlin.js.Date()
        val dayOfWeek = d.getDay().toInt()
        val diff = if (dayOfWeek == 0) 6 else dayOfWeek - 1
        d.asDynamic().setDate(d.getDate() - diff)
        return "${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}"
    }

    private fun monthStartDateString(): String {
        val d = kotlin.js.Date()
        return "${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-01"
    }
}

data class AppointmentStats(
    val todayCount: Int = 0,
    val weekCount: Int = 0,
    val pendingPayment: Int = 0,
    val confirmed: Int = 0,
    val monthTotal: Int = 0,
    val monthRevenue: Int = 0
)
